import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
    Tabs,
    Card,
    Form,
    Input,
    Radio,
    DatePicker,
    Button,
    Checkbox,
    Table,
} from "antd";
import { useEffect, useRef, useState } from "react";
import Layout from '../../Layout'
import { Activity, ClipboardList, Syringe, User, Printer, Save, } from "lucide-react";
import dayjs from "dayjs";
import SignatureCanvas from "react-signature-canvas";

const { TextArea } = Input;

export default function ConsultationForm() {
  const { t } = useTranslation();
    const diseases = [
        t('consultation_form.disease_kidney_liver'),
        t('consultation_form.disease_diabetes'),
        t('consultation_form.disease_blood_pressure'),
        t('consultation_form.disease_hepatitis'),
        t('consultation_form.disease_cardiovascular'),
        t('consultation_form.disease_epilepsy'),
        t('consultation_form.disease_medication'),
        t('consultation_form.disease_thyroid_cancer'),
        t('consultation_form.disease_other'),
    ];

    const sigRef = useRef<SignatureCanvas>(null);

    const clearSignature = () => {
        sigRef.current?.clear();
    };

    const [isDark, setIsDark] = useState(
        document.documentElement.classList.contains("dark")
    );

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains("dark"));
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
        });

        return () => observer.disconnect();
    }, []);

    const [data, setData] = useState(
        diseases.map((d, i) => ({
            key: i,
            disease: d,
            status: "no",
            note: "",
        }))
    );

    // const handleChange removed: duplicate declaration

    const handleNote = (index: number, value: string) => {
        const newData = [...data];
        newData[index].note = value;
        setData(newData);
    };

    const columns = [
        {
            title: t('consultation_form.col_disease'),
            dataIndex: "disease",
            width: 250,
        },
        {
            title: t('consultation_form.col_yes'),
            width: 80,
            render: (_: any, record: any, index: number) => (
                <Radio
                    checked={record.status === "yes"}
                    onChange={() => handleChange(index, "yes")}
                />
            ),
        },
        {
            title: t('consultation_form.col_no'),
            width: 100,
            render: (_: any, record: any, index: number) => (
                <Radio
                    checked={record.status === "no"}
                    onChange={() => handleChange(index, "no")}
                />
            ),
        },
        {
            title: t('consultation_form.col_explain'),
            render: (_: any, record: any, index: number) => (
                <Input
                    placeholder={t('consultation_form.col_explain_placeholder')}
                    value={record.note}
                    disabled={record.status === "no"}
                    onChange={(e) => handleNote(index, e.target.value)}
                />
            ),
        },
    ];

    const handleChange = (index: number, value: string) => {
        const newData = [...data];
        newData[index].status = value;
        setData(newData);
    };


    const weeks = [
        `01 ${t('consultation_form.week')}`,
        `02 ${t('consultation_form.week')}`,
        `03 ${t('consultation_form.week')}`,
        `04 ${t('consultation_form.week')}`,
        `05 ${t('consultation_form.week')}`,
        `06 ${t('consultation_form.week')}`,
        `07 ${t('consultation_form.week')}`,
        `08 ${t('consultation_form.week')}`,
    ];

    const [selectedWeek, setSelectedWeek] = useState("");

    const customerName = "Mr. John Doe";

    const tabItems = [
        {
            key: "1",
            label: (
                <div className="flex items-center gap-2">
                    <User size={16} />
                    <span>{t('consultation_form.tab_info')}</span>
                </div>
            ),
            children: (
                <div className="space-y-6">

                    <Card className="dark:bg-slate-800">

                        <h3 className="font-semibold mb-4 text-000000e0">{t('consultation_form.customer_info')}</h3>

                        <Form layout="vertical">

                            <div className="grid md:grid-cols-2 gap-4">

                                <Form.Item label={t('consultation_form.full_name')}>
                                    <Input />
                                </Form.Item>

                                <Form.Item label={t('consultation_form.gender')}>
                                    <Radio.Group>
                                        <Radio value="nam">{t('consultation_form.male')}</Radio>
                                        <Radio value="nu">{t('consultation_form.female')}</Radio>
                                    </Radio.Group>
                                </Form.Item>

                                <Form.Item label={t('consultation_form.dob')}>
                                    <DatePicker className="w-full" />
                                </Form.Item>

                                <Form.Item label={t('consultation_form.phone')}>
                                    <Input />
                                </Form.Item>

                                <Form.Item label={t('consultation_form.address')} className="md:col-span-2">
                                    <Input />
                                </Form.Item>

                                <Form.Item label={t('consultation_form.email')}>
                                    <Input />
                                </Form.Item>

                                <Form.Item label={t('consultation_form.current_job')}>
                                    <Input />
                                </Form.Item>

                            </div>

                        </Form>

                    </Card>


                    <Card className="dark:bg-slate-800">

                        <h3 className="font-semibold mb-4 text-000000e0">{t('consultation_form.customer_history')}</h3>

                        <Form layout="vertical">

                            <Form.Item label={t('consultation_form.weight_gain_duration')}>
                                <Input placeholder={t('consultation_form.weight_gain_placeholder')} />
                            </Form.Item>

                            <Form.Item label={t('consultation_form.weight_loss_method')}>
                                <TextArea rows={3} />
                            </Form.Item>

                            <Form.Item label={t('consultation_form.daily_habits')}>
                                <TextArea rows={4} />
                            </Form.Item>

                        </Form>

                        <div className="flex justify-end gap-3 mt-4">
                            <Button>{t('consultation_form.print')}</Button>
                            <Button type="primary">{t('consultation_form.save')}</Button>
                        </div>

                    </Card>

                </div>
            ),
        },

        {
            key: "2",
            label: (
                <div className="flex items-center gap-2">
                    <Activity size={16} />
                    <span>{t('consultation_form.tab_health')}</span>
                </div>
            ),
            children: (
                <Card className="dark:bg-slate-800">

                    <div className="bg-purple-50 text-purple-600 text-sm p-3 rounded mb-4">
                        {t('consultation_form.health_note')}
                    </div>

                    <Table
                        columns={columns}
                        dataSource={data}
                        pagination={false}
                        className="custom-ant-table"
                        scroll={{ x: "max-content" }}
                    />

                    <div className="mt-5 bg-green-50 dark:bg-green-900/30 p-3 rounded">
                        <Checkbox>
                            {t('consultation_form.confirm_truth')}
                        </Checkbox>
                    </div>

                    <div className="flex justify-end gap-3 mt-4">
                        <Button icon={<Printer size={16} />}>{t('consultation_form.print')}</Button>

                        <Button type="primary" icon={<Save size={16} />}>
                            {t('consultation_form.save')}
                        </Button>
                    </div>

                </Card>
            ),
        },

        {
            key: "3",
            label: (
                <div className="flex items-center gap-2">
                    <ClipboardList size={16} />
                    {t('consultation_form.tab_doctor')}
                </div>
            ),
            children: (
                <Card className="dark:bg-slate-800">

                    <div className="grid md:grid-cols-2 gap-6">

                        <div>
                            <h3 className="font-semibold mb-2 text-000000e0">
                                {t('consultation_form.treatment_program')}
                            </h3>

                            <Input.TextArea rows={8} />
                        </div>

                        <div>
                            <h3 className="font-semibold mb-3 text-000000e0">
                                {t('consultation_form.prescription')}
                            </h3>

                            <div className="grid grid-cols-2 gap-3">

                                {weeks.map((w) => (
                                    <button
                                        key={w}
                                        onClick={() => setSelectedWeek(w)}
                                        className={`
                                            h-[44px] rounded-lg border transition text-000000e0
                                            ${selectedWeek === w
                                                ? "bg-emerald-600 text-white border-emerald-600 shadow"
                                                : "bg-white dark:bg-slate-700 border-gray-300"}
                                            `}
                                    >
                                        {w}
                                    </button>
                                ))}

                            </div>
                        </div>

                    </div>

                    {/* Signature */}

                    <div className="mt-12 text-right">

                        <div className="text-sm mb-2 text-000000e0">
                            {customerName}
                        </div>

                        <div className="ml-auto w-[220px]">

                            {/* Canvas ký */}
                            <SignatureCanvas
                                key={isDark ? "dark" : "light"}
                                ref={sigRef}
                                penColor={isDark ? "#ffffff" : "#000000"}
                                canvasProps={{
                                    className:
                                        "border rounded w-full h-[120px] bg-white dark:bg-slate-900",
                                }}
                            />

                            <div className="border-t pt-2 text-sm text-000000e0">
                                {t('consultation_form.customer_signature')}
                            </div>

                            <Button
                                size="small"
                                className="mt-2"
                                onClick={clearSignature}
                            >
                                {t('consultation_form.clear_signature')}
                            </Button>

                        </div>

                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                        <Button>{t('consultation_form.print')}</Button>
                        <Button type="primary">{t('consultation_form.save')}</Button>
                    </div>

                </Card>
            ),
        },
    ];

    return (
        <Layout>
            <Helmet>
                <title>{t('page.consultation_form')} | Khothi</title>
            </Helmet>
            <div className="flex justify-between mb-6">

                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-600 text-white flex items-center justify-center rounded-lg">
                        <Syringe size={18} />
                    </div>

                    <div>
                        <h1 className="font-semibold text-lg text-000000e0">
                            {t('consultation_form.page_title')}
                        </h1>
                        <p className="text-sm text-gray-500">
                            {t('consultation_form.page_subtitle')}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <DatePicker
                        defaultValue={dayjs()}
                        format="DD/MM/YYYY"
                        className="min-w-[160px]"
                    />
                </div>

            </div>
            <Tabs items={tabItems} />
        </Layout>
    );
}